import CONSTANT from 'modules/constant'
import { API, headers } from './api'

export interface MemberByTierDto {
  tierName: string
  total: number
  tierColor: string
}

export interface CarRegistrationDataPointDto {
  label: string
  registration: number
  approved: number
  rejected: number
  pending: number
}

export interface VoucherDataPointDto {
  label: string
  issued: number
  redeemed: number
}

export interface PartnerVoucherDataPointDto {
  partnerName: string
  totalVoucher: number
  totalRedeemed: number
}

export interface VoucherChartDto {
  data: VoucherDataPointDto[]
}

export interface PartnerVoucherChartDto {
  data: PartnerVoucherDataPointDto[]
}

export interface LabelValueDto<T> {
  label: string
  value: T
  color?: string
}

export interface CarRegistrationChartDto {
  totalRegistered: number
  approved: number
  rejected: number
  pending: number
  conversionRate: number
  data: CarRegistrationDataPointDto[]
  registrationTier: LabelValueDto<number>[]
  topCarModel: LabelValueDto<number>[]
}

export interface CarInterestChartDto {
  carBrandOwnership: LabelValueDto<number>[]
  carModelOwnership: LabelValueDto<number>[]
  brandInterest: LabelValueDto<number>[]
  carTypeInterest: LabelValueDto<number>[]
  hyundaiModelInterest: LabelValueDto<number>[]
}

export interface BookingStatusSummaryDto {
  status: string
  count: number
  percentage: number
}

export interface MemberTierSummaryDto {
  tier: string
  count: number
  percentage: number
  color: string
}

export interface CategorySummaryDto {
  category: string
  count: number
  percentage: number
}

export interface CategoryRevenueDto {
  category: string
  revenue: number
}

export interface StatusCountDto {
  count: number
  percentage: number
  label: string
}

export interface CategoryStatusBreakdownDto {
  category: string
  total: number
  status: StatusCountDto[]
}

export interface CategoryRateDto {
  category: string
  rate: number
  total: number
  completed: number
}

export interface ReservationDashboardDto {
  totalBooking: number
  totalRevenue: number
  conversionRate: number
  visitDealerRate: number
  statusSummary: BookingStatusSummaryDto[]
  memberTierSummary: MemberTierSummaryDto[]
  revenueByStatus: CategoryRevenueDto[]
  serviceTypeSummary: CategorySummaryDto[]
  revenueByServiceType: CategoryRevenueDto[]
  completionRateByServiceType: CategoryRateDto[]
  serviceCategorySummary: CategorySummaryDto[]
  revenueByServicePackage: CategoryRevenueDto[]
  statusByServiceType: CategoryStatusBreakdownDto[]
  completionRateByServicePackage: CategoryRateDto[]
}

export interface TransactionHistoryDashboardDto {
  total: number
  conversionRate: number
  totalRevenue: number
  statusSummary: BookingStatusSummaryDto[]
  memberTierSummary: MemberTierSummaryDto[]
}

export interface CourtesyCarModelDataPointDto {
  vehicleModel: string
  carUse: number
}

export interface CourtesySeriesDto {
  label: string
  color: string
  data: number[]
}

export interface CourtesyChartDto {
  categories: string[]
  series: CourtesySeriesDto[]
  data?: CourtesyCarModelDataPointDto[]
}

export interface TradeInTierDataPointDto {
  tier: string
  tierColor: string
  hyundaiCount: number
  nonHyundaiCount: number
}

export interface TradeInChartDto {
  data: TradeInTierDataPointDto[]
}

export interface ReservationSummaryDto {
  totalBooking: number
}

export interface TransactionHistorySummaryDto {
  totalRevenue: number
  serviceRevenue: number
  accessoriesRevenue: number
}

export interface RVGSeriesDto {
  label: string
  color: string
  data: number[]
}

export interface RVGSubmissionChartDto {
  categories: string[]
  series: RVGSeriesDto[]
  topCarModels: LabelValueDto<number>[]
}

export interface CategorySegmentDto {
  name: string
  icon: string
  count: number
  percentage: number
}

export interface VoucherSegmentAnalysisDto {
  categories: CategorySegmentDto[]
}

export interface VoucherChannelDto {
  channelCode: string
  channelName: string
  count: number
  percentage: number
}

export interface VoucherChannelDistributionDto {
  totalTokens: number
  channels: VoucherChannelDto[]
}

export interface DailyTrendDto {
  date: string
  count: number
}

export interface TierBreakdownDto {
  label: string
  count: number
}

export interface DashboardTotalUsersDto {
  signUps: number
  signUpsChangePercent: number
  totalRegisteredUsers: number
  dailyTrend: DailyTrendDto[]
  byMemberTier: TierBreakdownDto[]
  byRegion: TierBreakdownDto[]
  byCarModel: TierBreakdownDto[]
}

export interface FirebaseEventDto {
  eventName: string
  eventCount: number
}

export interface FirebasePlatformDto {
  platform: string
  activeUsers: number
}

export interface FirebaseAnalyticsOverviewDto {
  isConfigured: boolean
  activeUsers: number
  activeUsersChangePercent: number
  newUsers: number
  sessions: number
  engagementRate: number
  topEvents: FirebaseEventDto[]
  platformBreakdown: FirebasePlatformDto[]
}

export interface FirebaseDailyMetricDto {
  date: string
  activeUsers: number
  newUsers: number
  sessions: number
}

export interface FirebaseDailyUsersDto {
  isConfigured: boolean
  data: FirebaseDailyMetricDto[]
}

export interface FirebaseFeatureAccessDto {
  featureName: string
  accessCount: number
}

export interface FirebaseMostAccessedFeatureDto {
  isConfigured: boolean
  features: FirebaseFeatureAccessDto[]
  totalFeatureAccesses: number
  totalFeatureAccessesChangePercent: number
  avgDailySessions: number
  peakDay: string | null
  peakDayCount: number
  mostActiveHour: string | null
}

export interface FirebaseActiveUserMetricsDto {
  isConfigured: boolean
  dailyActiveUsers: number
  weeklyActiveUsers: number
  monthlyActiveUsers: number
  dauMauRatio: number
  dailyTrend: FirebaseDailyMetricDto[]
}

export interface AppsflyerDailyInstallDto {
  date: string
  installs: number
  sessions: number
}

export interface AppsflyerAnalyticsDto {
  isConfigured: boolean
  installs: number
  installsChangePercent: number
  uninstalls: number
  uninstallsChangePercent: number
  sessions: number
  uniqueUsers: number
  topInAppEvents: FirebaseEventDto[]
  topCountries: TierBreakdownDto[]
}

export interface AppsflyerDailyInstallsDto {
  isConfigured: boolean
  data: AppsflyerDailyInstallDto[]
}

const dashboardApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getMembersByTier: builder.query<
      MemberByTierDto[],
      { carModelId?: number; cityId?: number; memberTiers?: string } | void
    >({
      query: (params) => {
        const search = new URLSearchParams()
        if (params?.carModelId != null) search.set('carModelId', String(params.carModelId))
        if (params?.cityId != null) search.set('cityId', String(params.cityId))
        if (params?.memberTiers) search.set('memberTiers', params.memberTiers)
        const qs = search.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_MEMBER_BY_TIER}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getCarRegistrationChart: builder.query<
      CarRegistrationChartDto,
      { startDate: string; endDate: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_CAR_REGISTRATION}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getVoucherChart: builder.query<VoucherChartDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_VOUCHER_CHART}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getPartnerVoucherChart: builder.query<
      PartnerVoucherChartDto,
      { startDate: string; endDate: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_PARTNER_VOUCHER_CHART}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getReservationDashboard: builder.query<
      ReservationDashboardDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)
        if (endDate) params.set('endDate', endDate)
        const qs = params.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_RESERVATION}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getTransactionHistoryDashboard: builder.query<
      TransactionHistoryDashboardDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)
        if (endDate) params.set('endDate', endDate)
        const qs = params.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_TRANSACTION_HISTORY}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getReservationSummary: builder.query<
      ReservationSummaryDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)
        if (endDate) params.set('endDate', endDate)
        const qs = params.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_RESERVATION_SUMMARY}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getTransactionHistorySummary: builder.query<
      TransactionHistorySummaryDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)
        if (endDate) params.set('endDate', endDate)
        const qs = params.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_TRANSACTION_HISTORY_SUMMARY}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getCourtesyChart: builder.query<CourtesyChartDto, { startDate?: string; endDate?: string; memberTiers?: string }>({
      query: ({ startDate, endDate, memberTiers }) => {
        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)
        if (endDate) params.set('endDate', endDate)
        if (memberTiers) params.set('memberTiers', memberTiers)
        const qs = params.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_COURTESY_CHART}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getTradeInChart: builder.query<TradeInChartDto, { startDate?: string; endDate?: string; memberTiers?: string }>({
      query: ({ startDate, endDate, memberTiers }) => {
        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)
        if (endDate) params.set('endDate', endDate)
        if (memberTiers) params.set('memberTiers', memberTiers)
        const qs = params.toString()
        return {
          url: `${CONSTANT.URL_DASHBOARD_TRADE_IN_CHART}${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getRVGSubmissionChart: builder.query<
      RVGSubmissionChartDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_RVG_SUBMISSION_CHART}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getCarInterestChart: builder.query<
      CarInterestChartDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_CAR_INTEREST_CHART}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getSegmentAnalysis: builder.query<
      VoucherSegmentAnalysisDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_SEGMENT_ANALYSIS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getSubSegmentAnalysis: builder.query<
      VoucherSegmentAnalysisDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_SUB_SEGMENT_ANALYSIS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getVoucherChannelDistribution: builder.query<
      VoucherChannelDistributionDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_VOUCHER_CHANNEL_DISTRIBUTION}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getSegmentTopCategory: builder.query<
      VoucherSegmentAnalysisDto,
      { startDate?: string; endDate?: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_SEGMENT_TOP_CATEGORY}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getTotalUsers: builder.query<DashboardTotalUsersDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_TOTAL_USERS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getFirebaseAnalytics: builder.query<FirebaseAnalyticsOverviewDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_FIREBASE_ANALYTICS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getFirebaseDailyUsers: builder.query<FirebaseDailyUsersDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_FIREBASE_DAILY_USERS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getFirebaseMostAccessedFeatures: builder.query<FirebaseMostAccessedFeatureDto, { startDate: string; endDate: string; topN?: number }>({
      query: ({ startDate, endDate, topN }) => {
        const params = new URLSearchParams({ startDate, endDate })
        if (topN) params.set('topN', String(topN))
        return {
          url: `${CONSTANT.URL_DASHBOARD_FIREBASE_MOST_ACCESSED}?${params.toString()}`,
          method: 'GET',
          headers: headers()
        }
      }
    }),
    getFirebaseActiveUsers: builder.query<FirebaseActiveUserMetricsDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_FIREBASE_ACTIVE_USERS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getAppsflyerAnalytics: builder.query<AppsflyerAnalyticsDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_APPSFLYER}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    }),
    getAppsflyerDailyInstalls: builder.query<AppsflyerDailyInstallsDto, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: `${CONSTANT.URL_DASHBOARD_APPSFLYER_DAILY_INSTALLS}?startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
        headers: headers()
      })
    })
  })
})

export const {
  useGetMembersByTierQuery,
  useGetCarRegistrationChartQuery,
  useGetVoucherChartQuery,
  useGetPartnerVoucherChartQuery,
  useGetReservationDashboardQuery,
  useGetTransactionHistoryDashboardQuery,
  useGetReservationSummaryQuery,
  useGetTransactionHistorySummaryQuery,
  useGetCourtesyChartQuery,
  useGetTradeInChartQuery,
  useGetRVGSubmissionChartQuery,
  useGetCarInterestChartQuery,
  useGetSegmentAnalysisQuery,
  useGetSubSegmentAnalysisQuery,
  useGetVoucherChannelDistributionQuery,
  useGetSegmentTopCategoryQuery,
  useGetTotalUsersQuery,
  useGetFirebaseAnalyticsQuery,
  useGetFirebaseDailyUsersQuery,
  useGetFirebaseMostAccessedFeaturesQuery,
  useGetFirebaseActiveUsersQuery,
  useGetAppsflyerAnalyticsQuery,
  useGetAppsflyerDailyInstallsQuery
} = dashboardApi
